import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { fetchArtworkById } from '../api/artworks';
import galleryData from '../data/gallery_metadata.json';
import { useInquiry } from '../contexts/InquiryContext';
import SEOHead from '../components/seo/SEOHead';
import './ArtworkDetailPage.css';

interface Artwork {
    id: string;
    title: string;
    imageUrl: string;
    images?: string[];
    description: string;
    price: number;
    year?: number;
    type?: string;
    dimensions?: string;
    status?: string;
}

const ArtworkDetailPage = () => {
    const { id } = useParams<{ id: string }>();
    const [artwork, setArtwork] = useState<Artwork | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeImage, setActiveImage] = useState(0);
    const { state, addItem, removeItem } = useInquiry();

    const isSelected = state.items.some(item => item.id === id);

    const toggleInquiry = () => {
        if (!artwork) return;
        if (isSelected) {
            removeItem(id);
        } else {
            const gData: any = galleryData;
            const item = gData.find((i: any) => artwork.imageUrl?.includes(i.filename));
            addItem({
                id: artwork.id,
                title: artwork.title,
                imageUrl: artwork.imageUrl,
                type: item?.category
            });
        }
    };

    useEffect(() => {
        window.scrollTo(0, 0);
        setActiveImage(0);
        const getArtwork = async () => {
            try {
                const data = await fetchArtworkById(id);
                setArtwork(data);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'An error occurred');
            } finally {
                setLoading(false);
            }
        };
        getArtwork();
    }, [id]);

    if (loading) return (
        <div className="min-h-screen flex items-center justify-center bg-white">
            <div className="animate-pulse font-serif text-gray-300 tracking-[0.3em] uppercase">Kunstlounge...</div>
        </div>
    );

    if (error || !artwork) return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-white">
            <h1 className="font-serif text-primary text-3xl mb-8">Werk niet gevonden</h1>
            <Link to="/" className="text-[10px] uppercase tracking-widest border-b border-primary pb-2">Terug naar collectie</Link>
        </div>
    );

    const galleryItems: any = galleryData;
    const masterMeta = galleryItems.find((item: any) =>
        artwork.imageUrl?.includes(item.filename) || item.id === id
    );

    const allImages: string[] = (artwork.images && artwork.images.length > 0)
        ? artwork.images
        : [artwork.imageUrl];

    const displayTitle = masterMeta?.title || artwork.title;
    const techYear = [artwork.type, artwork.year].filter(Boolean).join(', ');
    const seoTitle = `${displayTitle} – Clemens Briels${techYear ? ` ${techYear}` : ''} | Kunstlounge`;
    const seoDesc = masterMeta?.seo_description || artwork.description;

    const prevImage = () => setActiveImage(i => (i - 1 + allImages.length) % allImages.length);
    const nextImage = () => setActiveImage(i => (i + 1) % allImages.length);

    const hasRichContent = !!(masterMeta?.richDescription || masterMeta?.highlights);

    const artworkImageUrl = artwork.imageUrl.startsWith('http')
        ? artwork.imageUrl
        : `https://www.kunstlounge.nl${artwork.imageUrl}`;

    const artworkSchema = [{
        "@context": "https://schema.org",
        "@type": "VisualArtwork",
        "name": displayTitle,
        "description": seoDesc,
        "image": artworkImageUrl,
        "url": `https://www.kunstlounge.nl/artwork/${artwork.id}`,
        "creator": {
            "@type": "Person",
            "@id": "https://www.kunstlounge.nl/over-ons#clemens-briels",
            "name": "Clemens Briels"
        },
        ...(masterMeta?.year || artwork.year ? { "dateCreated": String(masterMeta?.year || artwork.year) } : {}),
        ...(masterMeta?.medium || artwork.type ? { "artMedium": masterMeta?.medium || artwork.type } : {}),
        ...(masterMeta?.edition ? { "artEdition": masterMeta.edition } : {}),
        ...(masterMeta?.certificate ? { "certificate": masterMeta.certificate } : {}),
        "isPartOf": {
            "@type": "Collection",
            "name": "Privécollectie Kunstlounge",
            "url": "https://www.kunstlounge.nl/#collectie"
        },
        "offers": {
            "@type": "Offer",
            "availability": artwork.status === 'sold' ? "https://schema.org/SoldOut" : "https://schema.org/InStock",
            "priceSpecification": {
                "@type": "PriceSpecification",
                "priceCurrency": "EUR",
                "description": masterMeta?.priceRange || "Prijs op aanvraag"
            },
            "seller": {
                "@type": "Organization",
                "name": "Kunstlounge",
                "url": "https://www.kunstlounge.nl"
            },
            "url": "https://www.kunstlounge.nl/aanvraag"
        }
    }];

    return (
        <main id="main-content" className="pt-28 pb-20 bg-white">
            <SEOHead
                title={seoTitle}
                description={seoDesc}
                canonical={`/artwork/${artwork.id}`}
                ogType="article"
                imageUrl={artwork.imageUrl}
                schemaData={artworkSchema}
                hreflangEn={`/en/artworks/${artwork.id}.html`}
                hreflangNl={`/artwork/${artwork.id}`}
            />

            {/* Breadcrumb */}
            <div className="container mx-auto px-6 mb-10">
                <nav className="flex items-center gap-3 text-[10px] uppercase tracking-widest text-gray-400" aria-label="Breadcrumb">
                    <Link to="/" className="hover:text-accent transition-colors">Collectie</Link>
                    <span className="w-4 h-px bg-gray-200" aria-hidden="true"></span>
                    <span className="text-primary truncate max-w-xs">{artwork.title}</span>
                </nav>
            </div>

            <div className="container mx-auto px-6">

                {/* === TOP: Gallery + Specs === */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start mb-24">

                    {/* Image Gallery */}
                    <div>
                        {/* Main image */}
                        <div className="relative bg-gray-50 overflow-hidden shadow-2xl">
                            <img
                                src={allImages[activeImage]}
                                alt={`${displayTitle}, afbeelding ${activeImage + 1} van ${allImages.length}`}
                                className="w-full object-contain transition-all duration-500 hover:brightness-105"
                                style={{ maxHeight: '580px' }}
                                loading="eager"
                                decoding="async"
                            />
                            {masterMeta?.dimensions && (
                                <div className="absolute bottom-4 right-4 bg-white/90 backdrop-blur-sm px-4 py-2 text-[10px] tracking-widest uppercase font-bold text-primary shadow-sm">
                                    {masterMeta.dimensions}
                                </div>
                            )}
                            {allImages.length > 1 && (
                                <>
                                    <button
                                        onClick={prevImage}
                                        aria-label="Vorige afbeelding"
                                        className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 backdrop-blur-sm flex items-center justify-center shadow-md hover:bg-accent hover:text-white transition-all duration-300 text-primary text-2xl"
                                    >
                                        ‹
                                    </button>
                                    <button
                                        onClick={nextImage}
                                        aria-label="Volgende afbeelding"
                                        className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 backdrop-blur-sm flex items-center justify-center shadow-md hover:bg-accent hover:text-white transition-all duration-300 text-primary text-2xl"
                                    >
                                        ›
                                    </button>
                                </>
                            )}
                        </div>

                        {/* Thumbnail strip */}
                        {allImages.length > 1 && (
                            <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
                                {allImages.map((img, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => setActiveImage(idx)}
                                        aria-label={`Toon afbeelding ${idx + 1}`}
                                        className={`flex-shrink-0 w-14 h-14 overflow-hidden border-2 transition-all duration-300 ${activeImage === idx ? 'border-accent' : 'border-transparent hover:border-gray-300'}`}
                                    >
                                        <img
                                            src={img}
                                            alt=""
                                            aria-hidden="true"
                                            className="w-full h-full object-cover"
                                            loading="lazy"
                                        />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Specs & CTA */}
                    <div className="flex flex-col">
                        {/* Urgency badge (e.g. "NERGENS MEER VERKRIJGBAAR") */}
                        {masterMeta?.badge && (
                            <div className="mb-6 inline-block bg-red-600 text-white text-[10px] uppercase tracking-[0.2em] font-bold px-4 py-2 self-start">
                                {masterMeta.badge}
                            </div>
                        )}

                        <div className="flex items-center gap-4 mb-6">
                            <span className="text-accent text-xs tracking-widest uppercase font-bold">Clemens Briels</span>
                            {masterMeta?.category && (
                                <>
                                    <span className="w-px h-4 bg-gray-200" aria-hidden="true"></span>
                                    <span className="text-gray-400 text-xs tracking-widest uppercase">{masterMeta.category}</span>
                                </>
                            )}
                        </div>

                        <h1 className="text-3xl md:text-5xl font-serif text-primary mb-10 leading-tight">
                            {displayTitle}
                        </h1>

                        {/* Specs table */}
                        <div className="border border-gray-100 mb-10">
                            <table className="w-full text-sm">
                                <tbody>
                                    <tr className="border-b border-gray-100">
                                        <td className="py-3 px-5 font-bold text-primary text-[11px] uppercase tracking-widest bg-gray-50 w-2/5">Titel</td>
                                        <td className="py-3 px-5 text-gray-600 font-light">{displayTitle}</td>
                                    </tr>
                                    <tr className="border-b border-gray-100">
                                        <td className="py-3 px-5 font-bold text-primary text-[11px] uppercase tracking-widest bg-gray-50">Gesigneerd</td>
                                        <td className="py-3 px-5 text-gray-600 font-light">{masterMeta?.signed ? 'Ja' : 'Op aanvraag'}</td>
                                    </tr>
                                    {(masterMeta?.dimensions || artwork.dimensions) && (
                                        <tr className="border-b border-gray-100">
                                            <td className="py-3 px-5 font-bold text-primary text-[11px] uppercase tracking-widest bg-gray-50">Afmeting</td>
                                            <td className="py-3 px-5 text-gray-600 font-light">{masterMeta?.dimensions || artwork.dimensions}</td>
                                        </tr>
                                    )}
                                    {(masterMeta?.year || artwork.year) && (
                                        <tr className="border-b border-gray-100">
                                            <td className="py-3 px-5 font-bold text-primary text-[11px] uppercase tracking-widest bg-gray-50">Jaar</td>
                                            <td className="py-3 px-5 text-gray-600 font-light">{masterMeta?.year || artwork.year}</td>
                                        </tr>
                                    )}
                                    {(masterMeta?.medium || artwork.type) && (
                                        <tr className="border-b border-gray-100">
                                            <td className="py-3 px-5 font-bold text-primary text-[11px] uppercase tracking-widest bg-gray-50">Techniek</td>
                                            <td className="py-3 px-5 text-gray-600 font-light">{masterMeta?.medium || artwork.type}</td>
                                        </tr>
                                    )}
                                    {masterMeta?.edition && (
                                        <tr className="border-b border-gray-100">
                                            <td className="py-3 px-5 font-bold text-primary text-[11px] uppercase tracking-widest bg-gray-50">Editie</td>
                                            <td className="py-3 px-5 text-gray-600 font-light">{masterMeta.edition}</td>
                                        </tr>
                                    )}
                                    {masterMeta?.frame && (
                                        <tr className="border-b border-gray-100">
                                            <td className="py-3 px-5 font-bold text-primary text-[11px] uppercase tracking-widest bg-gray-50">Lijst</td>
                                            <td className="py-3 px-5 text-gray-600 font-light">{masterMeta.frame}</td>
                                        </tr>
                                    )}
                                    {masterMeta?.condition && (
                                        <tr className="border-b border-gray-100">
                                            <td className="py-3 px-5 font-bold text-primary text-[11px] uppercase tracking-widest bg-gray-50">Staat</td>
                                            <td className="py-3 px-5 text-gray-600 font-light">{masterMeta.condition}</td>
                                        </tr>
                                    )}
                                    {masterMeta?.certificate && (
                                        <tr className="border-b border-gray-100">
                                            <td className="py-3 px-5 font-bold text-primary text-[11px] uppercase tracking-widest bg-gray-50">Certificaat van echtheid</td>
                                            <td className="py-3 px-5 text-gray-600 font-light">{masterMeta.certificate}</td>
                                        </tr>
                                    )}
                                    <tr>
                                        <td className="py-3 px-5 font-bold text-primary text-[11px] uppercase tracking-widest bg-gray-50">Investering</td>
                                        <td className="py-3 px-5 font-serif text-primary">
                                            {masterMeta?.priceRange
                                                ? <><span className="font-semibold">{masterMeta.priceRange}</span><span className="ml-3 text-gray-400 text-[11px] uppercase tracking-widest">exacte prijs op aanvraag</span></>
                                                : <span className="italic text-gray-500">Op aanvraag</span>
                                            }
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <p className="text-sm text-gray-500 font-light mb-6 leading-relaxed">
                            Bemachtig dit unieke kunstwerk voor uw collectie en informeer naar de beschikbaarheid.{' '}
                            <Link to="/aanvraag" className="text-accent hover:underline">Gebruik dan het contactformulier.</Link>
                        </p>

                        <button
                            onClick={toggleInquiry}
                            disabled={artwork.status === 'sold'}
                            className={`${artwork.status === 'sold' ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : isSelected ? 'bg-accent shadow-accent/20' : 'bg-primary shadow-2xl hover:bg-accent text-white'} px-10 py-5 transition-all duration-500 uppercase text-[10px] tracking-[0.3em] font-bold text-center mb-4`}
                        >
                            {artwork.status === 'sold' ? 'Niet meer beschikbaar' : isSelected ? 'In aanvraaglijst ✓' : 'Informatie aanvragen'}
                        </button>

                        {isSelected && (
                            <Link to="/aanvraag" className="block text-center text-[10px] uppercase tracking-widest text-accent font-bold hover:underline">
                                Ga naar aanvraaglijst →
                            </Link>
                        )}
                    </div>
                </div>

                {/* === Rich Description (only when masterMeta has richDescription/highlights) === */}
                {hasRichContent && (
                    <section className="max-w-4xl mx-auto mb-20 border-t border-gray-100 pt-20" aria-labelledby="description-heading">
                        <h2 id="description-heading" className="text-3xl font-serif text-primary mb-12">
                            Over dit werk
                        </h2>
                        <div className="space-y-6 font-light text-gray-600 leading-relaxed text-lg">
                            {masterMeta.richDescription.map((paragraph: string, idx: number) => (
                                <p key={idx}>{paragraph}</p>
                            ))}
                        </div>

                        {masterMeta.highlights && (
                            <div className="mt-16 bg-gray-50 p-10 border border-gray-100">
                                <h3 className="text-2xl font-serif text-primary mb-8">Waarom dit werk?</h3>
                                <ul className="space-y-5">
                                    {masterMeta.highlights.map((h: { label: string; text: string }, idx: number) => (
                                        <li key={idx} className="flex gap-4 items-start">
                                            <span className="text-accent mt-1 flex-shrink-0 text-lg">✦</span>
                                            <span className="text-gray-600 font-light leading-relaxed">
                                                <strong className="font-serif text-primary">{h.label}: </strong>{h.text}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                                <div className="mt-10 text-center">
                                    <p className="text-accent font-serif italic text-xl mb-6">
                                        Bemachtig dit unieke kunstwerk voor uw collectie en informeer naar de beschikbaarheid.
                                    </p>
                                    <button
                                        onClick={toggleInquiry}
                                        className={`${isSelected ? 'bg-accent' : 'bg-primary hover:bg-accent'} text-white px-12 py-5 transition-all duration-500 uppercase text-[10px] tracking-[0.3em] font-bold`}
                                    >
                                        {isSelected ? 'In aanvraaglijst ✓' : 'Informatie aanvragen'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </section>
                )}

                {/* === Fallback AEO section for artworks without richDescription === */}
                {!hasRichContent && (
                    <section className="mt-20 pt-20 border-t border-gray-100">
                        <div className="max-w-3xl">
                            <h2 className="text-2xl font-serif text-primary mb-10">Waarom dit werk in uw collectie?</h2>
                            <div className="space-y-8 font-light text-gray-500 leading-relaxed">
                                <p>
                                    Dit specifieke werk van <strong>Clemens Briels</strong> belichaamt de essentie van het <em>Antipodisme</em>. Door het gebruik van levendige kleuren en een unieke kijk op perspectief, haalt u niet alleen een kunstwerk in huis, maar een bron van dagelijkse inspiratie en optimisme.
                                </p>
                                <p>
                                    Elk werk uit onze privécollectie is zorgvuldig geselecteerd op kwaliteit, conditie en artistieke waarde. Als u investeert in een Briels via Kunstlounge, bent u verzekerd van een authentiek meesterwerk met een bewezen herkomst.
                                </p>
                            </div>
                            <div className="mt-12 flex gap-10">
                                <Link to="/antipodisme" className="text-[10px] uppercase tracking-widest border-b border-gray-200 pb-2 hover:border-accent hover:text-accent transition-all">Over de stijl</Link>
                                <Link to="/over-ons" className="text-[10px] uppercase tracking-widest border-b border-gray-200 pb-2 hover:border-accent hover:text-accent transition-all">Over de herkomst</Link>
                            </div>
                        </div>
                    </section>
                )}

                {/* Bottom nav */}
                <div className="border-t border-gray-100 pt-12 mt-16 flex flex-wrap gap-8">
                    <Link to="/antipodisme" className="text-[10px] uppercase tracking-widest border-b border-gray-200 pb-2 hover:border-accent hover:text-accent transition-all">Over de stijl</Link>
                    <Link to="/over-ons" className="text-[10px] uppercase tracking-widest border-b border-gray-200 pb-2 hover:border-accent hover:text-accent transition-all">Over de herkomst</Link>
                    <Link to="/" className="text-[10px] uppercase tracking-widest border-b border-gray-200 pb-2 hover:border-accent hover:text-accent transition-all">Terug naar collectie</Link>
                </div>

            </div>
        </main>
    );
};

export default ArtworkDetailPage;
